import { Phone, Mail, MapPin, Clock, ShieldCheck } from "lucide-react";
import { CONTACT_INFO, IMAGES } from "../data";
import ContactForm from "../components/ContactForm";
import Meta from "../components/Meta";
import { motion } from "framer-motion";
import { fadeIn, fadeInSlow, slideInLeft, slideInRight, staggerContainer, staggerItem } from "../lib/animations";

export default function Kontakt() {
  const openingHours = [
    { days: "Mandag - Torsdag", hours: "07:00 - 16:00" },
    { days: "Fredag", hours: "07:00 - 14:30" },
    { days: "Lørdag - Søndag", hours: "Lukket" },
  ];

  return (
    <>
      <Meta
        title="Kontakt"
        description="Kontakt Nordsjællands Tømrer og Snedker i Helsingør. Ring til os på 20949740 eller send en mail til kontakt@nsts.dk for et uforpligtende tilbud."
      />

      {/* Page Header Banner */}
      <section className="relative pt-40 pb-24 bg-zinc-950 text-white rounded-none overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Kontakt Nordsjællands Tømrer og Snedker i Helsingør"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-zinc-950" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 block mb-3">
            Uforpligtende tilbud
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light uppercase tracking-wide text-stone-50">
            Kontakt <span className="font-semibold text-amber-500">os i dag</span>
          </h1>
          <p className="mt-4 text-stone-300 max-w-2xl font-light leading-relaxed text-sm sm:text-base">
            Har du spørgsmål, eller ønsker du et uforpligtende tilbud på dit næste projekt? Ring, send en e-mail eller udfyld formularen herunder.
          </p>
        </motion.div>
      </section>

      {/* Main Content Section */}
      <section className="bg-stone-50 py-24 px-6 md:px-12 text-zinc-900 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left Column: Contact details & info */}
          <motion.div
            variants={slideInLeft}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-5 space-y-10"
          >
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-700">
                Kontaktoplysninger
              </span>
              <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-wide text-zinc-900">
                Vi er klar til at hjælpe dig
              </h2>
              <p className="text-stone-600 text-sm leading-relaxed font-light">
                Vi besvarer alle henvendelser hurtigst muligt, med henblik på at aftale en besigtigelse af opgaven.
              </p>
            </div>

            {/* Contact cards */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-zinc-100 border border-zinc-200 flex items-center justify-center text-amber-700 shrink-0">
                  <Phone className="w-4 h-4" />
                </div>
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-stone-500">Ring direkte</span>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="text-sm font-bold text-zinc-900 hover:text-amber-700 transition-colors duration-200 whitespace-nowrap"
                  >
                    {CONTACT_INFO.phoneFormatted}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-zinc-100 border border-zinc-200 flex items-center justify-center text-amber-700 shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-stone-500">Send e-mail</span>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="text-sm font-semibold text-zinc-900 hover:text-amber-700 transition-colors duration-200 whitespace-nowrap"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-zinc-100 border border-zinc-200 flex items-center justify-center text-amber-700 shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-stone-500">Adresse</span>
                  <span className="text-sm font-semibold text-zinc-900 leading-relaxed">
                    {CONTACT_INFO.address}
                  </span>
                </div>
              </div>
            </div>

            {/* Opening hours */}
            <div className="bg-white border border-stone-200 p-6 rounded-none space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-900 flex items-center gap-2 border-b border-stone-100 pb-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <span>Arbejdstider</span>
              </h3>
              <ul className="space-y-2 text-xs">
                {openingHours.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center py-1 border-b border-stone-50 last:border-0">
                    <span className="text-stone-500 font-medium">{item.days}</span>
                    <span className="text-zinc-900 font-semibold">{item.hours}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company metadata */}
            <div className="flex items-center gap-2 text-xs text-stone-500">
              <ShieldCheck className="w-4 h-4 text-amber-700" />
              <span>Nordsjællands Tømrer og Snedker · CVR: {CONTACT_INFO.cvr}</span>
            </div>
          </motion.div>

          {/* Right Column: Contact form */}
          <motion.div
            variants={slideInRight}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-7 bg-zinc-950 text-white p-8 md:p-12 border border-zinc-800 rounded-none"
          >
            <h3 className="text-lg font-bold uppercase tracking-wider text-stone-100 mb-2">
              Send os en besked
            </h3>
            <p className="text-xs text-stone-400 leading-relaxed font-light mb-8">
              Udfyld formularen herunder med dine kontaktoplysninger og en kort beskrivelse af dit projekt, så vender vi tilbage med henblik på at aftale en besigtigelse af opgaven.
            </p>
            <ContactForm />
          </motion.div>
        </div>
      </section>
    </>
  );
}
